// Angular
import { Injectable } from '@angular/core';
// RxJS
import { BehaviorSubject } from 'rxjs';
// Layout
import { LayoutConfigModel } from '../../core/_base/layout';

export interface ClassType {
  header: string[];
  header_mobile: string[];
  header_menu: string[];
  aside_menu: string[];
}

@Injectable()
export class HtmlClassService {
  // Public properties
  config: LayoutConfigModel;
  classes: ClassType;
  onClassesUpdated$: BehaviorSubject<ClassType>;

  // Private properties
  private loaded: string[] = [];

  /**
   * Component constructor
   */
  constructor() {
    this.onClassesUpdated$ = new BehaviorSubject(this.classes);
  }

  /**
   * Build html element classes from layout config
   * @param layoutConfig
   */
  setConfig(layoutConfig: LayoutConfigModel) {
    this.config = layoutConfig;

    // scope list of classes
    this.classes = {
      header: [],
      header_mobile: [],
      header_menu: [],
      aside_menu: [],
    };

    // init base layout
    this.initLayout();
    this.initLoader();

    // init header and subheader menu
    this.initHeader();
    this.initSubheader();

    // init aside and aside menu
    this.initAside();

    // init footer
    this.initFooter();

    this.initSkins();

    this.onClassesUpdated$.next(this.classes);
  }

  /**
   * Returns Classes
   *
   * @param path: string
   * @param toString boolean
   */
  getClasses(path?: keyof ClassType, toString?: boolean): ClassType | string[] | string {
    if (path) {
      const classes = this.classes[path] || '';
      if (toString && Array.isArray(classes)) {
        return classes.join(' ');
      }
      return classes.toString();
    }
    return this.classes;
  }

  /**
   * Init Layout
   */
  private initLayout() {
    const selfBodyClass = this.config?.self?.body?.class?.toString();
    if (selfBodyClass) {
      const bodyClasses: string[] = selfBodyClass.split(' ');
      bodyClasses.forEach(cssClass => document.body.classList.add(cssClass));
    }

    if (this.config?.self?.layout === 'boxed' && this.config?.self?.body?.['background-image']) {
      document.body.style.backgroundImage = `url("${this.config.self.body['background-image']}")`;
    }

    // Offcanvas directions
    document.body.classList.add('kt-quick-panel--right');
    document.body.classList.add('kt-demo-panel--right');
    document.body.classList.add('kt-offcanvas-panel--right');
  }

  /**
   * Init Loader
   */
  private initLoader() {}

  /**
   * Init Header
   */
  private initHeader() {
    // Fixed header
    if (this.config?.header?.self?.fixed?.desktop) {
      document.body.classList.add('kt-header--fixed');
      this.classes.header.push('kt-header--fixed');
    } else {
      document.body.classList.add('kt-header--static');
    }

    if (this.config?.header?.self?.fixed?.mobile) {
      document.body.classList.add('kt-header-mobile--fixed');
      this.classes.header_mobile.push('kt-header-mobile--fixed');
    }

    const layout = this.config?.header?.menu?.self?.layout;
    if (layout) {
      this.classes.header_menu.push(`kt-header-menu--layout-${layout}`);
    }
  }

  /**
   * Init Subheader
   */
  private initSubheader() {
    if (this.config?.subheader?.fixed) {
      document.body.classList.add('kt-subheader--fixed');
    }

    if (this.config?.subheader?.display) {
      document.body.classList.add('kt-subheader--enabled');
    }

    const style = this.config?.subheader?.style;
    if (style) {
      document.body.classList.add(`kt-subheader--${style}`);
    }
  }

  /**
   * Init Aside
   */
  private initAside() {
    if (this.config?.aside?.self?.display !== true) {
      return;
    }

    document.body.classList.add('kt-aside--enabled');

    if (this.config?.aside?.self?.fixed) {
      document.body.classList.add('kt-aside--fixed');
      this.classes.header.push('kt-aside--fixed');
    } else {
      document.body.classList.add('kt-aside--static');
    }

    if (this.config?.aside?.self?.minimize?.default) {
      document.body.classList.add('kt-aside--minimize');
    }

    if (this.config?.aside?.menu?.dropdown) {
      this.classes.aside_menu.push('kt-aside-menu--dropdown');
    }
  }

  /**
   * Init Footer
   */
  private initFooter() {
    if (this.config?.footer?.self?.fixed) {
      document.body.classList.add('kt-footer--fixed');
    }
  }

  /**
   * Set the body class name based on page skin options
   */
  private initSkins() {
    const headerSkin = this.config?.header?.self?.skin;
    if (headerSkin) {
      document.body.classList.add(`kt-header-base-${headerSkin}`);
    }

    const headerMenuSkin = this.config?.header?.menu?.desktop?.submenu?.skin;
    if (headerMenuSkin) {
      document.body.classList.add(`kt-header-menu-${headerMenuSkin}`);
    }

    const brandSkin = this.config?.brand?.self?.skin;
    if (brandSkin) {
      document.body.classList.add(`kt-brand-${brandSkin}`);
    }

    const asideSkin = this.config?.aside?.self?.skin;
    if (asideSkin) {
      document.body.classList.add(`kt-aside-${asideSkin}`);
    }
  }
}
